import { useEffect, useState } from 'react';
import {
  RiAddLine,
  RiAlertLine,
  RiCheckLine,
  RiDeleteBin6Line,
  RiQuestionLine,
  RiShieldCheckLine,
} from 'react-icons/ri';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { useStore } from '../../state/store';
import type { RequirementLevel, RequirementPriority, RequirementStatus } from '../../state/domain';
import { cn, formatDay } from '@/lib/utils';

const LEVEL_OPTIONS: { value: RequirementLevel; label: string; hint: string }[] = [
  { value: 'pir', label: 'PIR', hint: '优先情报需求：决定行动方向的核心问题' },
  { value: 'eei', label: 'EEI', hint: '必要信息要素：回答 PIR 所需的具体信息' },
  { value: 'sir', label: 'SIR', hint: '特定信息需求：单点核实事项' },
];

const PRIORITY_LABEL: Record<RequirementPriority, string> = {
  low: '低',
  medium: '中',
  high: '高',
  critical: '紧急',
};

const STATUS_LABEL: Record<RequirementStatus, string> = {
  open: '待答复',
  partial: '部分答复',
  answered: '已答复',
  dropped: '已放弃',
};

const STATUS_VARIANT: Record<RequirementStatus, 'cord' | 'assess' | 'evidence' | 'muted'> = {
  open: 'cord',
  partial: 'assess',
  answered: 'evidence',
  dropped: 'muted',
};

export function RequirementPanel() {
  const requirements = useStore(state => state.requirements);
  const coverage = useStore(state => state.requirementCoverage);
  const activeTargetId = useStore(state => state.activeTargetId);
  const evidence = useStore(state => state.evidence);
  const addRequirement = useStore(state => state.addRequirement);
  const updateRequirement = useStore(state => state.updateRequirement);
  const deleteRequirement = useStore(state => state.deleteRequirement);

  const [form, setForm] = useState({
    level: 'pir' as RequirementLevel,
    question: '',
    priority: 'high' as RequirementPriority,
    dueAt: '',
  });
  const [answerDraft, setAnswerDraft] = useState<Record<string, string>>({});

  useEffect(() => {
    setAnswerDraft({});
  }, [requirements.length]);

  return (
    <div className="flex flex-col gap-2.5 p-3">
      <div className="flex items-center justify-between">
        <span className="label-caps">情报需求与缺口</span>
        <Badge variant="muted">{requirements.length} 条</Badge>
      </div>

      {coverage && (
        <div className="rounded-[var(--radius)] border border-border bg-card p-2.5">
          <div className="flex items-center justify-between text-[11px]">
            <span className="text-muted-foreground">需求覆盖度</span>
            <span className="font-mono font-medium">{coverage.coverage}%</span>
          </div>
          <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-[var(--color-evidence)] transition-all"
              style={{ width: `${coverage.coverage}%` }}
            />
          </div>
          <div className="mt-2 grid grid-cols-4 gap-1 text-center">
            <Chip label="待答复" value={coverage.open} warn={coverage.open > 0} />
            <Chip label="高优先未答" value={coverage.highOpen} warn={coverage.highOpen > 0} />
            <Chip label="逾期" value={coverage.overdue} warn={coverage.overdue > 0} />
            <Chip label="已答复" value={coverage.answered} />
          </div>
        </div>
      )}

      <div className="rounded-[var(--radius)] border border-border bg-card p-2.5">
        <div className="mb-2 flex items-center gap-1.5 text-[11.5px] font-medium">
          <RiAddLine className="size-3.5 text-primary" />
          登记需求
        </div>
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-3 gap-1">
            {LEVEL_OPTIONS.map(option => (
              <button
                key={option.value}
                type="button"
                title={option.hint}
                onClick={() => setForm({ ...form, level: option.value })}
                className={cn(
                  'rounded-[var(--radius-sm)] border py-1 text-[10.5px]',
                  form.level === option.value
                    ? 'border-primary bg-primary/8 text-primary'
                    : 'border-border text-muted-foreground hover:bg-accent'
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
          <Label>待回答的问题 *</Label>
          <Textarea
            rows={2}
            value={form.question}
            placeholder="例：该对象的资金审批权限边界在哪里？"
            onChange={e => setForm({ ...form, question: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <Label>优先级</Label>
              <select
                className="h-8 w-full rounded-[var(--radius-sm)] border border-input bg-card px-2 text-[12px]"
                value={form.priority}
                onChange={e => setForm({ ...form, priority: e.target.value as RequirementPriority })}
              >
                {(Object.keys(PRIORITY_LABEL) as RequirementPriority[]).map(key => (
                  <option key={key} value={key}>
                    {PRIORITY_LABEL[key]}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <Label>答复期限</Label>
              <Input
                type="date"
                value={form.dueAt}
                onChange={e => setForm({ ...form, dueAt: e.target.value })}
              />
            </div>
          </div>
          <Button
            size="sm"
            disabled={!form.question.trim()}
            onClick={async () => {
              await addRequirement({
                level: form.level,
                question: form.question.trim(),
                priority: form.priority,
                targetId: activeTargetId,
                dueAt: form.dueAt ? new Date(form.dueAt).toISOString() : null,
                status: 'open',
              });
              setForm({ ...form, question: '', dueAt: '' });
            }}
          >
            登记需求
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        {requirements.map(item => {
          const overdue =
            item.status !== 'answered' &&
            item.status !== 'dropped' &&
            item.dueAt &&
            new Date(item.dueAt).getTime() < Date.now();
          const relatedEvidence = evidence.filter(e => item.evidence.includes(e.id));
          return (
            <div
              key={item.id}
              className={cn(
                'rounded-[var(--radius)] border bg-card p-2.5',
                overdue ? 'border-[var(--color-cord)]/40' : 'border-border'
              )}
              style={{ borderLeftWidth: 3, borderLeftColor: overdue ? 'var(--color-cord)' : 'var(--color-primary)' }}
            >
              <div className="flex items-center gap-1.5">
                <Badge variant="default" className="font-mono">
                  {item.code ?? item.level.toUpperCase()}
                </Badge>
                <Badge variant={STATUS_VARIANT[item.status]}>{item.statusLabel}</Badge>
                <Badge variant={item.priority === 'critical' || item.priority === 'high' ? 'cord' : 'muted'}>
                  {PRIORITY_LABEL[item.priority]}
                </Badge>
                {overdue && (
                  <Badge variant="cord" className="gap-1">
                    <RiAlertLine className="size-3" />
                    逾期
                  </Badge>
                )}
                <Button
                  size="icon-sm"
                  variant="ghost"
                  className="ml-auto shrink-0 text-muted-foreground hover:text-destructive"
                  onClick={() => deleteRequirement(item.id)}
                >
                  <RiDeleteBin6Line className="size-3" />
                </Button>
              </div>

              <div className="mt-1.5 flex items-start gap-1.5">
                <RiQuestionLine className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
                <p className="text-[11.5px] leading-relaxed">{item.question}</p>
              </div>

              {item.dueAt && (
                <div className="mt-1 text-[10px] text-muted-foreground">期限 {formatDay(item.dueAt)}</div>
              )}

              <Textarea
                rows={2}
                className="mt-1.5 text-[11px]"
                placeholder="答复要点…"
                value={answerDraft[item.id] ?? item.answer ?? ''}
                onChange={e => setAnswerDraft({ ...answerDraft, [item.id]: e.target.value })}
              />

              {relatedEvidence.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {relatedEvidence.map(e => (
                    <Badge key={e.id} variant="evidence" className="gap-1 font-mono">
                      <RiShieldCheckLine className="size-3" />
                      {e.sourceReliability}
                      {e.infoCredibility}
                    </Badge>
                  ))}
                </div>
              )}

              <div className="mt-1.5 flex items-center gap-1">
                <span className="mr-auto text-[10px] text-muted-foreground">
                  关联证据 {item.evidence.length} 条
                </span>
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() =>
                    updateRequirement(item.id, {
                      answer: answerDraft[item.id] ?? item.answer ?? '',
                      status: 'partial',
                    })
                  }
                >
                  标记部分答复
                </Button>
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() =>
                    updateRequirement(item.id, {
                      answer: answerDraft[item.id] ?? item.answer ?? '',
                      status: 'answered',
                    })
                  }
                >
                  <RiCheckLine className="size-3" />
                  答复完成
                </Button>
              </div>
            </div>
          );
        })}
        {requirements.length === 0 && (
          <div className="rounded-[var(--radius)] border border-dashed border-border p-5 text-center text-[11.5px] text-muted-foreground">
            尚未登记情报需求。先写清「要回答什么问题」，采集与分析才有方向。
          </div>
        )}
      </div>
    </div>
  );
}

function Chip({ label, value, warn }: { label: string; value: number; warn?: boolean }) {
  return (
    <div className={cn('rounded-[var(--radius-sm)] bg-muted/60 py-1', warn && 'bg-[var(--color-cord)]/8')}>
      <div className={cn('font-mono text-[12px] font-semibold', warn && 'text-[var(--color-cord)]')}>{value}</div>
      <div className="text-[9px] text-muted-foreground">{label}</div>
    </div>
  );
}